import {
  renderTemplatePreview,
  TemplateFieldType,
  type TemplateField,
  type TemplateSchema,
} from '@/api';
import { evaluateCondition } from '@/composables/contractGeneration/useConditionalVisibility';
import { extractErrorDetail } from '@/utils/errorHandler';

/**
 * Failures the contract-generation endpoints distinguish by HTTP status:
 *   - 409 → template is still being validated; caller should poll/retry.
 *   - 422 → the request was refused; `detail` is either a list of template
 *           validation failures or a single sentence saying what is missing
 *           (no destination folder, a draft that is already saved, …).
 * Anything else lands as `kind: 'unknown'`.
 *
 * Shared by the stateless preview and the draft lifecycle (`DraftsService`),
 * which reach the same templates through the same gates on the backend.
 */
export type RenderErrorKind = 'still_validating' | 'validation_failed' | 'unknown';

export class RenderError extends Error {
  readonly kind: RenderErrorKind;
  readonly messages: string[];

  constructor(kind: RenderErrorKind, message: string, messages: string[] = []) {
    super(message);
    this.name = 'RenderError';
    this.kind = kind;
    this.messages = messages.length > 0 ? messages : [message];
  }
}

/** The shape an SDK call returns with `throwOnError: false`. */
export interface SdkLikeResponse {
  data?: unknown;
  error?: unknown;
  response?: { status?: number };
}

export function buildRenderError(response: SdkLikeResponse, fallback: string): RenderError {
  const status = response.response?.status;
  const detail = (response.error as { detail?: unknown } | undefined)?.detail;

  if (status === 409) {
    const msg =
      typeof detail === 'string' ? detail : 'Template is still validating, please retry shortly.';
    return new RenderError('still_validating', msg);
  }

  if (status === 422) {
    if (Array.isArray(detail)) {
      const list = detail.map(item => String(item)).filter(Boolean);
      return new RenderError(
        'validation_failed',
        list.length > 0 ? list.join('; ') : fallback,
        list
      );
    }
    if (typeof detail === 'string') {
      return new RenderError('validation_failed', detail, [detail]);
    }
  }

  return new RenderError('unknown', extractErrorDetail(response.error, fallback));
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  return false;
}

/**
 * Strip hidden/blank optional values from the form data so the payload
 * matches the backend's expected `context_data` shape. `localScopes` is the
 * chain of enclosing scopes from innermost to outermost (e.g.
 * `[milestone, project]` inside a nested list). Bare conditions like
 * `"is_international"` walk that chain before falling back to the root —
 * keeps this walker in lockstep with the rendering-time visibility check.
 */
function buildFieldContext(
  field: TemplateField,
  value: unknown,
  rootFormData: Record<string, unknown>,
  localScopes: unknown[]
): unknown {
  const conditionAst = field.condition_ast;
  if (conditionAst && !evaluateCondition(conditionAst, localScopes, rootFormData)) {
    return undefined;
  }

  if (field.type === TemplateFieldType.OBJECT) {
    const children = field.fields ?? {};
    const result: Record<string, unknown> = {};
    const source = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
    let hasAny = false;
    for (const [childKey, childField] of Object.entries(children)) {
      const childValue = buildFieldContext(childField, source[childKey], rootFormData, [
        source,
        ...localScopes,
      ]);
      if (childValue !== undefined) {
        result[childKey] = childValue;
        hasAny = true;
      }
    }
    if (!hasAny && !field.required) return undefined;
    return result;
  }

  if (field.type === TemplateFieldType.LIST) {
    const items = Array.isArray(value) ? value : [];
    const itemFields = field.item_fields;
    if (itemFields) {
      return items
        .map(item => {
          const source = (item && typeof item === 'object' ? item : {}) as Record<string, unknown>;
          const rendered: Record<string, unknown> = {};
          for (const [childKey, childField] of Object.entries(itemFields)) {
            const childValue = buildFieldContext(childField, source[childKey], rootFormData, [
              source,
              ...localScopes,
            ]);
            if (childValue !== undefined) {
              rendered[childKey] = childValue;
            }
          }
          return rendered;
        })
        .filter(item => Object.keys(item).length > 0 || field.required);
    }
    return items.filter(item => !isBlank(item));
  }

  if (isBlank(value)) {
    return field.required ? value : undefined;
  }

  return value;
}

export function buildContextData(
  schema: TemplateSchema,
  formData: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, field] of Object.entries(schema.fields ?? {})) {
    // Root fields start with just the root form data as their only scope.
    const rendered = buildFieldContext(field, formData[key], formData, [formData]);
    if (rendered !== undefined) {
      result[key] = rendered;
    }
  }
  return result;
}

export const RenderService = {
  /**
   * Render a preview. Returns a Blob containing the rendered .docx.
   *
   * Throws a `RenderError` for the gated cases so callers can distinguish
   * "still validating" from "validation failed" from generic failures.
   */
  async preview(variantUid: string, contextData: Record<string, unknown>): Promise<Blob> {
    // Opt out of the client's global `throwOnError` so we get the response
    // object back and can map the backend's status + `detail` into a typed
    // RenderError (otherwise the raw throw loses the message).
    const response = (await renderTemplatePreview({
      body: {
        template_variant_uid: variantUid,
        context_data: contextData,
      },
      throwOnError: false,
    })) as SdkLikeResponse;

    if (response.error) {
      throw buildRenderError(response, 'Failed to render preview.');
    }

    if (!response.data) {
      throw new RenderError('unknown', 'Preview response was empty');
    }

    if (response.data instanceof Blob) {
      return response.data;
    }

    throw new RenderError('unknown', 'Unexpected preview response type');
  },
};
